import { PlanTrailActionContext, getMap, getLayerRegistry } from "./types";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import { Route } from "../../../../../utils/models/Route";
import { deriveMasterTrail } from "../../../utils/masterTrail";

const SOURCE_ID = 'debug-master-trail';
const LAYER_ID = 'debug-master-trail';

/**
 * Reference-trail overlay: draws the reference trail as a dashed line
 * distinct from every real route color, so a user can see exactly what their
 * locked days are sliced from (and confirm the rendered day routes' on-trail
 * portions still line up with it). Gated behind `state.debugMasterTrailVisible`
 * — off by default, toggled via "Show reference trail" in the Actions
 * panel's Tools section (only enabled while `state.routeLocked`, since it
 * has nothing meaningful to show otherwise).
 *
 * While locked, this draws the FROZEN trail (state.lockedMasterTrail) —
 * the actual immutable slicing source — rather than re-deriving it from the
 * current day routes, which may already contain baked-in detours (that
 * would make this overlay just mirror the divergence it's meant to catch).
 */
export const masterTrailDebugActions = {
  updateMasterTrailDebugLayer({ state, getters }: PlanTrailActionContext) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();

    // Always clear first — cheapest way to avoid a stale line lingering when
    // toggled off or when the trail becomes undeliverable (e.g. a branch).
    layerRegistry.removeLayer(LAYER_ID);
    if (map.getSource(SOURCE_ID)) {
      map.removeSource(SOURCE_ID);
    }

    if (!state.debugMasterTrailVisible) return;

    let coordinates: number[][];
    if (state.routeLocked && state.lockedMasterTrail) {
      coordinates = state.lockedMasterTrail.coordinates;
    } else {
      const routes = getters['routesArray'] as Route[];
      coordinates = deriveMasterTrail(routes).coordinates;
    }
    if (coordinates.length < 2) return;
    const trail = { coordinates };

    layerRegistry.addSource(SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: trail.coordinates },
      },
    });

    layerRegistry.addLayer({
      id: LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#00e5ff',
        'line-width': 3,
        'line-opacity': 0.9,
        'line-dasharray': [1, 2],
      },
    }, MapLayerCategory.Top, { z: 20 });
  },

  async toggleMasterTrailDebug({ state, dispatch }: PlanTrailActionContext) {
    // Session-only — never written to localStorage, so it can never turn
    // itself on automatically for a later plan/reload/import.
    state.debugMasterTrailVisible = !state.debugMasterTrailVisible;
    await dispatch('updateMasterTrailDebugLayer');
  },
};
